import React, { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Edit3,
  Eye,
  MoreVertical,
  Trash2,
} from "lucide-react";
import FilterButton from "../Common/FilterButton";
import FilterPopover from "../Common/FilterPopover";
import SearchBar from "../Common/SearchBar";
import "./PremiumListPage.css";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];


const getValue = (row, column) => {
  if (column.render) return column.render(row);
  return row[column.key] || "-";
};

const PremiumListPage = ({
  title,
  subtitle,
  actionLabel,
  onCreate,
  stats = [],
  columns = [],
  rows = [],
  searchPlaceholder = "Search...",
  emptyTitle = "No records found",
  onView,
  onEdit,
  onDelete,
  extraActions,
  filterFields,
}) => {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const resolvedFilterFields = useMemo(() => {
    if (filterFields) return filterFields;
    return columns
      .filter((column) => column.key && !column.render)
      .slice(0, 4)
      .map((column) => {
        const options = Array.from(
          new Set(rows.map((row) => row[column.key]).filter((value) => value !== undefined && value !== null && value !== ""))
        ).slice(0, 25);
        return {
          key: column.key,
          label: column.label,
          options: options.map((value) => ({ value: String(value), label: String(value) })),
        };
      })
      .filter((field) => field.options.length > 1);
  }, [columns, filterFields, rows]);
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const filteredRows = useMemo(() => {
    const search = query.trim().toLowerCase();
    return rows.filter((row) =>
      (!search || columns.some((column) => String(row[column.key] || "").toLowerCase().includes(search))) &&
      resolvedFilterFields.every((field) => {
        const filterValue = filters[field.key];
        if (!filterValue) return true;
        const rowValue = field.getValue ? field.getValue(row) : row[field.key];
        if (field.match === "contains") {
          return String(rowValue || "").toLowerCase().includes(String(filterValue).toLowerCase());
        }
        return String(rowValue || "").toLowerCase() === String(filterValue).toLowerCase();
      })
    );
  }, [columns, filters, query, resolvedFilterFields, rows]);

  const totalPages = Math.max(Math.ceil(filteredRows.length / pageSize), 1);
  const safePage = Math.min(page, totalPages);
  const visibleRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const firstVisiblePage = Math.max(1, Math.min(safePage - 1, totalPages - 3));
  const visiblePageNumbers = Array.from(
    { length: Math.min(totalPages, 4) },
    (_, index) => firstVisiblePage + index
  );
  const handleRowClick = (event, row) => {
    if (!onView) return;
    const interactiveTarget = event.target.closest("button, input, a, select, textarea");
    if (interactiveTarget && !interactiveTarget.classList.contains("premium-link")) return;
    onView(row);
  };

  return (
    <section className="premium-list-page">
      <div className="premium-page-head">
        <div>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {actionLabel && onCreate && (
          <button className="premium-primary-btn" onClick={onCreate}>
            {actionLabel}
          </button>
        )}
      </div>

      <div className="premium-stat-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article className="premium-summary-card" key={stat.label}>
              <span className={`premium-summary-icon ${stat.tone || "blue"}`}>
                <Icon size={22} />
              </span>
              <div>
                <small>{stat.label}</small>
                <strong>{stat.value}</strong>
                <span>{stat.subtitle || "All time"}</span>
              </div>
            </article>
          );
        })}
      </div>

      <div className="premium-table-card">
        <div className="premium-table-toolbar">
          <label>
            Show
            <select
              name="pageSize"
              value={pageSize}
              aria-label="Entries per page"
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option value={size} key={size}>{size}</option>
              ))}
            </select>
            entries
          </label>
          <div>
            {resolvedFilterFields.length > 0 && (
              <span className="zarp-filter-wrap">
                <FilterButton
                  active={hasActiveFilters}
                  open={filtersOpen}
                  onClick={() => setFiltersOpen((current) => !current)}
                />
                <FilterPopover
                  open={filtersOpen}
                  fields={resolvedFilterFields}
                  filters={filters}
                  onChange={(key, value) => {
                    setFilters((current) => ({ ...current, [key]: value }));
                    setPage(1);
                  }}
                  onReset={() => {
                    setFilters({});
                    setPage(1);
                  }}
                  onClose={() => setFiltersOpen(false)}
                />
              </span>
            )}
            <SearchBar
              value={query}
              onChange={(value) => {
                setQuery(value);
                setPage(1);
              }}
              placeholder={searchPlaceholder}
              className="premium-table-search"
            />
          </div>
        </div>

        <div className="premium-table-wrap">
          <table className="premium-data-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" />
                </th>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr
                  key={row.id || row.invoiceNumber || row.quoteNumber || index}
                  className={onView ? "is-clickable" : ""}
                  onClick={(event) => handleRowClick(event, row)}
                >
                  <td>
                    <input type="checkbox" />
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className={column.className || ""}>
                      {getValue(row, column)}
                    </td>
                  ))}
                  <td>
                    <div className="premium-row-actions">
                      {onView && (
                        <button onClick={() => onView(row)} title="View">
                          <Eye size={16} />
                        </button>
                      )}
                      {onEdit && (
                        <button onClick={() => onEdit(row)} title="Edit">
                          <Edit3 size={16} />
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(row)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      )}
                      {extraActions?.(row)}
                      <button title="More">
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {visibleRows.length === 0 && <div className="premium-empty-state">{emptyTitle}</div>}
        </div>

        <div className="premium-mobile-list">
          {visibleRows.map((row, index) => (
            <article
              className={`premium-mobile-row ${onView ? "is-clickable" : ""}`}
              key={row.id || row.invoiceNumber || row.quoteNumber || index}
              onClick={(event) => handleRowClick(event, row)}
            >
              <div>
                <strong>{getValue(row, columns[1] || columns[0])}</strong>
                <small>{getValue(row, columns[2] || columns[0])}</small>
              </div>
              <div className="premium-row-actions">
                {onView && <button onClick={() => onView(row)}><Eye size={16} /></button>}
                {onEdit && <button onClick={() => onEdit(row)}><Edit3 size={16} /></button>}
                {onDelete && <button onClick={() => onDelete(row)}><Trash2 size={16} /></button>}
              </div>
            </article>
          ))}
        </div>

        <div className="premium-pagination">
          <span>
            Showing {filteredRows.length ? (safePage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(safePage * pageSize, filteredRows.length)} of {filteredRows.length} entries
          </span>
          <div>
            <button disabled={safePage === 1} onClick={() => setPage((current) => current - 1)}>
              <ChevronLeft size={16} />
            </button>
            {visiblePageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                className={safePage === pageNumber ? "is-active" : ""}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button disabled={safePage === totalPages} onClick={() => setPage((current) => current + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export const StatusBadge = ({ children, tone = "green" }) => (
  <span className={`premium-status ${tone}`}>{children || "-"}</span>
);

export default PremiumListPage;
